/**
 * PostHog (EU-Region) – Webanalyse + Sitzungsaufzeichnung (Session Replay).
 * Wird ausschließlich nach Analytics-Einwilligung geladen; bei Widerruf wird die
 * Aufzeichnung gestoppt und das Tracking abgeschaltet. Formulareingaben werden maskiert.
 * Projekt-Schlüssel ist öffentlich (Client-Key), Projekt: PostHog EU „Kreul Media Group".
 */
const SCRIPT_ID = "pb24-posthog-script";
const SCRIPT_SRC = "https://eu-assets.i.posthog.com/static/array.js";
const API_HOST = "https://eu.i.posthog.com";
const KEY = process.env.REACT_APP_POSTHOG_KEY || "phc_xTH4cb8XfxmwieEmBAFBvmg52ymJNVXz8ABhy9JJQhtY";

let enabled = false;
let initialized = false;
let loading = false;
let lastUrl = "";

const INTERN_KEY = "kmg_intern_v1";

/**
 * Interne Geräte (Team) nie in der Statistik (Kevin 28.10.2026): Flag im Browser, gesetzt bei jeder Admin-Anmeldung
 * oder per Link ?intern=1 (Aufheben: ?intern=0). IP-Filter geht nicht, PostHog anonymisiert IPs.
 */
export function isInternalDevice() {
  try {
    const q = new URLSearchParams(window.location.search).get("intern");
    if (q === "1") localStorage.setItem(INTERN_KEY, "1");
    if (q === "0") localStorage.removeItem(INTERN_KEY);
    // Wer Admin, Werkstatt oder Kreul Live öffnet, gehört zum Team (auch ohne Anmeldung in diesem Browser)
    if (/^\/(admin|werkstatt|live)(\/|$)/.test(window.location.pathname)) localStorage.setItem(INTERN_KEY, "1");
    return localStorage.getItem(INTERN_KEY) === "1";
  } catch (_) { return false; }
}

export function markInternalDevice() {
  try { localStorage.setItem(INTERN_KEY, "1"); } catch (_) { /* noop */ }
  enabled = false;
  const p = ph();
  if (p && initialized) {
    try { p.stopSessionRecording?.(); p.opt_out_capturing?.(); } catch (_) { /* noop */ }
  }
}

function isPreviewOrTestContext() {
  if (isInternalDevice()) return true;
  const host = (window.location.hostname || "").toLowerCase();
  const ua = (navigator.userAgent || "").toLowerCase();
  return host === "localhost"
    || host === "127.0.0.1"
    || host.endsWith(".preview.emergentagent.com")
    || host.endsWith(".emergentagent.com")
    || /(playwright|headlesschrome|puppeteer|selenium)/.test(ua);
}

function ph() {
  return typeof window !== "undefined" ? window.posthog : null;
}

function initOnce() {
  const p = ph();
  if (!p || initialized) return;
  try {
    p.init(KEY, {
      api_host: API_HOST,
      person_profiles: "identified_only",
      capture_pageview: false, // SPA-PageViews feuert der Dispatcher
      capture_pageleave: true,
      autocapture: true,
      session_recording: { maskAllInputs: true, maskTextSelector: "[data-ph-mask]" },
      loaded: () => { sendPageView(); },
    });
    initialized = true;
  } catch (_) { /* Tracking darf die App nie stören */ }
}

function sendPageView() {
  const p = ph();
  if (!enabled || !initialized || !p) return;
  const url = window.location.href;
  if (url === lastUrl) return;
  lastUrl = url;
  try { p.capture("$pageview", { $current_url: url }); } catch (_) { /* noop */ }
}

/** Einwilligungsstand anwenden: laden/starten oder stoppen. */
export function syncPosthogConsent(hasAnalyticsConsent) {
  if (typeof window === "undefined" || !KEY) return;
  enabled = Boolean(hasAnalyticsConsent) && !isPreviewOrTestContext();
  const p = ph();
  if (!enabled) {
    if (p && initialized) {
      try { p.stopSessionRecording?.(); p.opt_out_capturing?.(); } catch (_) { /* noop */ }
    }
    return;
  }
  if (p && initialized) {
    try { p.opt_in_capturing?.(); p.startSessionRecording?.(); } catch (_) { /* noop */ }
    sendPageView();
    return;
  }
  if (loading || document.getElementById(SCRIPT_ID)) return;
  loading = true;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = SCRIPT_SRC;
  script.onload = () => { loading = false; initOnce(); };
  script.onerror = () => { loading = false; };
  document.head.appendChild(script);
}

const PERSONAL = /mail|phone|tel|name|street|strasse|address|adresse|zip|plz|city|ort|message|nachricht|note|notiz|iban|company|firma/i;

/**
 * Einzelnes Ereignis an PostHog (nur mit Statistik-Einwilligung, nie von Team-Geräten).
 * Persönliche Angaben werden herausgefiltert, nur einfache Werte gehen raus (28.10.2026).
 */
export function capturePosthog(name, props = {}) {
  const p = ph();
  if (!enabled || !initialized || !p || !name) return;
  const clean = {};
  try {
    Object.entries(props || {}).forEach(([k, v]) => {
      if (PERSONAL.test(k)) return;
      if (["string", "number", "boolean"].includes(typeof v)) clean[k] = typeof v === "string" ? v.slice(0, 120) : v;
    });
    p.capture(name, clean);
  } catch (_) { /* noop */ }
}

/** Bei SPA-Navigation aufrufen. */
export function trackPosthogRoute() {
  sendPageView();
}
